import { useState, useEffect } from 'react';
import { getServiceClass } from '../../services/serviceCache';
import type { PlanningContext } from '../../services/autoPopulationService';
import type { Requirements } from '../../services/blueprintGeneratorService';
import toast from 'react-hot-toast';
import { Loader2, Sparkles, CheckCircle2, Info, ArrowRight } from 'lucide-react';

interface SmartPlanningInterfaceProps {
  projectId: string;
  userId: string;
  onBlueprintGenerated: (blueprintId: string) => void;
}

export function SmartPlanningInterface({
  projectId,
  userId,
  onBlueprintGenerated,
}: SmartPlanningInterfaceProps) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [context, setContext] = useState<PlanningContext | null>(null);
  const [_userInput, _setUserInput] = useState('');
  const [requirements, setRequirements] = useState<Requirements>({
    projectName: '',
    description: '',
    features: [''],
    constraints: [],
    targetAudience: '',
    timeline: '',
    budget: '',
    techStack: {
      frontend: '',
      backend: '',
      database: '',
      infrastructure: '',
    },
  });

  useEffect(() => {
    loadContext();
  }, [projectId]);

  const loadContext = async () => {
    try {
      setLoading(true);
      const AutoPopulationService = await getServiceClass('autoPopulationService', 'AutoPopulationService');
      const planningContext = await AutoPopulationService.getProjectContext(projectId);
      setContext(planningContext);

      if (planningContext.hasAnalysis && planningContext.suggestedRequirements) {
        setRequirements(planningContext.suggestedRequirements as Requirements);
      }
    } catch (error) {
      const { logger } = await import('../../services/loggingService');
      logger.error('Failed to load context', { error, projectId });
      toast.error('Failed to load project context');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!requirements.projectName.trim()) {
      toast.error('Project name is required');
      return;
    }

    if (!requirements.description.trim()) {
      toast.error('Project description is required');
      return;
    }

    if (requirements.features.filter(f => f.trim()).length === 0) {
      toast.error('At least one feature is required');
      return;
    }

    setGenerating(true);

    try {
      const cleanRequirements: Requirements = {
        ...requirements,
        features: requirements.features.filter(f => f.trim()),
        constraints: requirements.constraints?.filter(c => c.trim()) || [],
      };

      const BlueprintGeneratorService = await getServiceClass('blueprintGeneratorService', 'BlueprintGeneratorService');
      const blueprint = await BlueprintGeneratorService.generateFromRequirements(
        userId,
        projectId,
        cleanRequirements,
        'anthropic'
      );

      const AutoPopulationService = await getServiceClass('autoPopulationService', 'AutoPopulationService');
      await AutoPopulationService.markProjectAsAutoPopulated(projectId);

      toast.success('Blueprint generated successfully!');
      onBlueprintGenerated(blueprint.id);
    } catch (error) {
      const { logger } = await import('../../services/loggingService');
      logger.error('Failed to generate blueprint', { error, projectId, userId });
      toast.error((error as Error).message || 'Failed to generate blueprint');
    } finally {
      setGenerating(false);
    }
  };

  const handleAddFeature = () => {
    setRequirements(prev => ({
      ...prev,
      features: [...prev.features, ''],
    }));
  };

  const handleRemoveFeature = (index: number) => {
    setRequirements(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleFeatureChange = (index: number, value: string) => {
    setRequirements(prev => ({
      ...prev,
      features: prev.features.map((f, i) => (i === index ? value : f)),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-accent-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {context?.hasAnalysis && context.isImported && (
        <div className="mb-8 alert alert-default rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-primary-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-primary mb-2">
                Repository Analyzed Successfully
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                We've analyzed your repository and detected the following:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-surface rounded-lg p-4 border border-border">
                  <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                    Project Type
                  </div>
                  <div className="font-semibold text-text">
                    {context.detectedInfo?.projectType || 'Application'}
                  </div>
                </div>

                <div className="bg-surface rounded-lg p-4 border border-border">
                  <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                    Tech Stack
                  </div>
                  <div className="font-semibold text-text">
                    {context.detectedInfo?.frameworkStack || 'Modern Stack'}
                  </div>
                </div>
              </div>

              {context.detectedInfo && (
                <div className="flex flex-wrap gap-2">
                  {context.detectedInfo.hasTests && (
                    <span className="badge-success inline-flex items-center gap-1 text-xs">
                      <CheckCircle2 className="w-3 h-3" />
                      Tests
                    </span>
                  )}
                  {context.detectedInfo.hasDocumentation && (
                    <span className="badge-success inline-flex items-center gap-1 text-xs">
                      <CheckCircle2 className="w-3 h-3" />
                      Documentation
                    </span>
                  )}
                  {context.detectedInfo.hasCI && (
                    <span className="badge-success inline-flex items-center gap-1 text-xs">
                      <CheckCircle2 className="w-3 h-3" />
                      CI/CD
                    </span>
                  )}
                </div>
              )}

              {context.recommendations && context.recommendations.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-start gap-2 text-sm text-secondary">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium">Recommendations: </span>
                      {context.recommendations[0]}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 px-8 py-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {context?.hasAnalysis ? 'What Would You Like to Build?' : 'Project Requirements'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {context?.hasAnalysis
              ? 'Tell us what you want to add or extend in your existing project'
              : 'Describe your project vision and we\'ll generate a comprehensive blueprint'}
          </p>
        </div>

        <div className="p-8 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Project Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={requirements.projectName}
              onChange={(e) => setRequirements(prev => ({ ...prev, projectName: e.target.value }))}
              className="input-base w-full focus:ring-primary"
              placeholder="e.g., E-commerce Platform"
              disabled={context?.hasAnalysis}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-text mb-2">
              {context?.hasAnalysis ? 'What do you want to accomplish?' : 'Project Description'}{' '}
              <span className="text-destructive">*</span>
            </label>
            <textarea
              value={requirements.description}
              onChange={(e) => setRequirements(prev => ({ ...prev, description: e.target.value }))}
              rows={5}
              className="input-base w-full focus:ring-primary"
              placeholder={
                context?.hasAnalysis
                  ? 'e.g., Add a subscription system with tiered plans, or Implement a real-time chat feature...'
                  : 'Describe what your project does, who it\'s for, and what problems it solves...'
              }
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
              Key Features <span className="text-destructive">*</span>
            </label>
            <div className="space-y-3">
              {requirements.features.map((feature, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className="input-base flex-1 focus:ring-primary"
                    placeholder={`Feature ${index + 1}`}
                  />
                  {requirements.features.length > 1 && (
                    <button
                      onClick={() => handleRemoveFeature(index)}
                      className="btn-destructive-outline"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={handleAddFeature}
              className="btn-secondary mt-3"
            >
              + Add Feature
            </button>
          </div>

          {context?.hasAnalysis && requirements.techStack && (
            <div className="bg-background-secondary rounded-lg p-6 border border-border">
              <h3 className="font-semibold text-text mb-4">
                Detected Tech Stack (Optional Changes)
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Frontend
                  </label>
                  <input
                    type="text"
                    value={requirements.techStack.frontend || ''}
                    onChange={(e) => setRequirements(prev => ({
                      ...prev,
                      techStack: { ...prev.techStack, frontend: e.target.value },
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., React, Vue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Backend
                  </label>
                  <input
                    type="text"
                    value={requirements.techStack.backend || ''}
                    onChange={(e) => setRequirements(prev => ({
                      ...prev,
                      techStack: { ...prev.techStack, backend: e.target.value },
                    }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Node.js, Python"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {context?.hasAnalysis
                ? 'We\'ll use your existing architecture and patterns as the foundation'
                : 'We\'ll generate a comprehensive blueprint based on your requirements'}
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn btn-primary inline-flex items-center gap-2 px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-sm"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating Blueprint...
                </>
              ) : (
                <>
                  Generate Blueprint
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
